import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "41";
const height = "40";
const viewBox = "0 0 41 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path fill="url(#pattern7)" d="M0 0h40.714v40H0z" key="key-0" />
        <defs key="key-1">
            <pattern
                id="pattern7"
                width="1"
                height="1"
                patternContentUnits="objectBoundingBox"
            >
                <use transform="scale(.0091 .00926)" xlinkHref="#image7" />
            </pattern>

            <image
                id="image7"
                width="110"
                height="108"
                xlinkHref="data:image/png;base64,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"
            />
        </defs>
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Word"
});
